import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, ChevronRight } from 'lucide-react';
import { Drawer } from '@kollez/design-system';

interface WorkflowRow {
  id: string; name: string; kind: string; owner: string; v: number; stages: string[]; used: number; conv: string; status: 'Live' | 'Draft' | 'Retired';
}

const SEED: WorkflowRow[] = [
  { id: 'campus-std', name: 'Campus drive · standard', kind: 'Campus drive', owner: 'Placement office', v: 4, stages: ['Apply', 'Eligibility', 'Test', 'Interview', 'Offer'], used: 6, conv: '412 → 21 (5.1%)', status: 'Live' },
  { id: 'campus-bulk', name: 'Campus drive · IT services (bulk)', kind: 'Campus drive', owner: 'Company', v: 2, stages: ['Apply', 'Eligibility', 'Test', 'Group discussion', 'HR interview', 'Offer'], used: 3, conv: '1,180 → 96 (8.1%)', status: 'Live' },
  { id: 'product-sample', name: 'Product company · work sample first', kind: 'Campus drive', owner: 'Company', v: 1, stages: ['Apply', 'Chat screening', 'Test', 'Work sample', 'Panel', 'Offer'], used: 1, conv: '96 → 4 (4.2%)', status: 'Live' },
  { id: 'winter-intern', name: 'Winter internship', kind: 'Internship', owner: 'Placement office', v: 3, stages: ['Apply', 'Eligibility', 'Chat screening', 'Interview', 'Offer'], used: 2, conv: '240 → 34 (14%)', status: 'Live' },
  { id: 'hack', name: 'Hackathon entry', kind: 'Programme', owner: 'Department', v: 2, stages: ['Team registration', 'Idea note', 'Shortlist', 'Event'], used: 1, conv: '86 teams → 48', status: 'Live' },
  { id: 'onboard-1', name: 'First-year onboarding', kind: 'Admission', owner: 'Group office', v: 5, stages: ['Enrol', 'Baseline exam', 'Support grouping'], used: 1, conv: '1,420 → placed in groups', status: 'Live' },
  { id: 'day0', name: 'Day 0 · one-day tournament', kind: 'Campus drive', owner: 'Company', v: 1, stages: ['Apply', 'Test', 'Work sample', 'Panel', 'Offer same day'], used: 0, conv: '', status: 'Draft' },
  { id: 'campus-v3', name: 'Campus drive · standard (v3)', kind: 'Campus drive', owner: 'Placement office', v: 3, stages: ['Apply', 'Eligibility', 'Test', 'Interview', 'Offer'], used: 0, conv: '980 → 44 (4.5%)', status: 'Retired' },
];

const SEGMENTS = [['all', 'All'], ['Campus drive', 'Drives'], ['Internship', 'Internships'], ['Programme', 'Hackathons and training'], ['Admission', 'Admissions']] as const;

export function WorkflowLibraryPage() {
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState<string>('all');
  const [detail, setDetail] = useState<WorkflowRow | null>(null);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return SEED.filter((r) => (seg === 'all' || r.kind === seg) && (!ql || `${r.name} ${r.kind}`.toLowerCase().includes(ql)));
  }, [q, seg]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Placement · Workflows</p>
          <h1 className="h-page">Workflows</h1>
          <p className="subhead">The steps a candidate goes through, from applying to joining. Build one per kind of opportunity, reuse it everywhere, and change it without touching a live run.</p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-primary" to="/workflows/builder"><Plus size={14} />New workflow</Link>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">In use now</span><span className="kpi-value">6</span><span className="kpi-meta">across 14 open opportunities</span></div>
        <div className="card kpi" data-tone="success"><span className="kpi-label">Median time to offer</span><span className="kpi-value">11 days</span><span className="kpi-meta">Was 24 days last season</span></div>
        <div className="card kpi" data-tone="warning"><span className="kpi-label">Candidates waiting on a stage</span><span className="kpi-value">38</span><span className="kpi-meta">9 past the promised date</span></div>
        <div className="card kpi"><span className="kpi-label">Feedback sent</span><span className="kpi-value">96%</span><span className="kpi-meta">Everyone who was turned down</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Search size={14} />
            <input className="input" placeholder="Workflow name" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="segmented">
            {SEGMENTS.map(([v, l]) => (<button key={v} aria-pressed={seg === v} onClick={() => setSeg(v)}>{l}</button>))}
          </div>
          <span className="fb-count">{shown.length} shown</span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Workflow</th><th className="hide-mobile">Stages</th><th className="r">Used by</th><th className="hide-mobile">Applicants → offers</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="cell-title">{r.name}</div>
                    <div className="cell-sub">{r.kind} · v{r.v} · {r.owner}</div>
                  </td>
                  <td className="hide-mobile">
                    <div className="flex flex-wrap gap-1">
                      {r.stages.map((s) => <span key={s} className="qtype">{s}</span>)}
                    </div>
                  </td>
                  <td className="r">{r.used ? `${r.used} open` : <span className="cell-sub">—</span>}</td>
                  <td className="hide-mobile">{r.conv || <span className="cell-sub">Not run yet</span>}</td>
                  <td><span className={`badge ${r.status === 'Live' ? 'badge-success' : r.status === 'Draft' ? 'badge-warning' : ''}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.name ?? ''}
        size="lg"
      >
        {detail && (
          <>
            <p className="eyebrow">{detail.kind} · version {detail.v} · owned by {detail.owner}</p>
            <div className="grid grid-cols-3 gap-3 mt-3 text-center">
              <div className="well p-3"><div className="cell-sub">Stages</div><div className="h-serif" style={{ fontSize: '1.5rem' }}>{detail.stages.length}</div></div>
              <div className="well p-3"><div className="cell-sub">Open now</div><div className="h-serif" style={{ fontSize: '1.5rem' }}>{detail.used}</div></div>
              <div className="well p-3"><div className="cell-sub">Version</div><div className="h-serif" style={{ fontSize: '1.5rem' }}>v{detail.v}</div></div>
            </div>
            <h3 className="text-sm font-semibold mt-4 mb-2">The path</h3>
            <div className="flex flex-wrap items-center gap-2">
              {detail.stages.map((s, i) => (
                <span key={s} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight size={12} />}
                  <span className="qtype">{s}</span>
                </span>
              ))}
            </div>
            <dl className="dl mt-4">
              <dt>Promise</dt><dd>{detail.kind === 'Campus drive' ? 'A result within 5 days of each stage, and a reason if it ends' : 'A result within 3 days of each stage'}</dd>
              <dt>Candidate effort</dt><dd>{detail.stages.includes('Work sample') ? 'About 5 hours in total (work sample capped at 3)' : 'About 2 hours in total'}</dd>
              <dt>Last changed</dt><dd>2 Sept by Srinivas Rao K.</dd>
            </dl>
            <div className="drawer-foot">
              <button className="btn btn-ghost" onClick={() => setDetail(null)}>Duplicate</button>
              <Link className="btn btn-outline ml-auto" to="/workflows/run">See a live run</Link>
              <Link className="btn btn-primary" to="/workflows/builder">Open</Link>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
